import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";

import { CFormGroup, CLabel } from "@coreui/react";

import { CListboxMulti } from "./CListboxMulti";

export const CSelectMultiple = ({
  value = [],
  options = [],
  onChange,
  className,
  label,
  disabled,
  required,
  placeholder = "Chọn...",
  isOptionDisabled,
  onChangeCallback,
  maxVisibleItems = 3,
}) => {
  //#region Data
  const _class = classNames("c-input", className);

  const [open, setOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  const selectedOptions = useMemo(() => {
    if (!value || value.length === 0) return [];
    return options.filter((opt) => value.includes(opt.value));
  }, [value, options]);

  const shouldExpand = useMemo(
    () => selectedOptions.length > maxVisibleItems,
    [selectedOptions, maxVisibleItems],
  );

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open]);

  // Calculate popup position
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [open]);
  //#endregion

  //#region Event
  const handleRemoveItem = (e, valueToRemove) => {
    e.stopPropagation();
    if (disabled) return;

    const newValues = (value || []).filter((v) => v !== valueToRemove);
    const newSelectedOptions = selectedOptions.filter(
      (opt) => opt.value !== valueToRemove,
    );

    onChange?.(newValues, newSelectedOptions);
    onChangeCallback?.(newValues, newSelectedOptions);
  };
  //#endregion

  //#region Render
  return (
    <CFormGroup className={_class}>
      {label && (
        <CLabel>
          {label} {required && <span className="text-danger">*</span>}
        </CLabel>
      )}
      <div ref={triggerRef}>
        <div
          className={classNames(
            "flex flex-row items-center px-[12px] py-1.5 border border-gray-200 rounded-lg",
            "aria-invalid:border-error",
            "hover:not-[aria-disabled=true]:border-primary/60",
            "focus-within:border-primary/60",
            "aria-disabled:bg-black/5 aria-disabled:opacity-80 aria-disabled:border-gray-300",
            "transition-all duration-200",
            shouldExpand && "min-h-11 py-2", // Tối thiểu chiều cao khi expand và thêm padding Y
            disabled ? "cursor-default" : "cursor-pointer",
          )}
          aria-disabled={disabled}
          onClick={() => !disabled && setOpen(!open)}
        >
          <div
            className={classNames(
              "flex-1 font-medium font-inter text-text",
              shouldExpand
                ? "flex flex-wrap gap-1.5 py-0.5"
                : "flex items-center gap-1.5 overflow-x-auto",
            )}
          >
            {selectedOptions.length > 0 ? (
              selectedOptions.map((opt) => (
                <span
                  key={opt.value}
                  style={{ border: "1px solid black" }}
                  className={classNames(
                    "inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-md",
                    "bg-blue-50 text-blue-600 text-[15px] font-medium",
                    "!border-blue-300",
                    "whitespace-nowrap",
                    "transition-all duration-150",
                  )}
                >
                  <span>{opt.label}</span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => handleRemoveItem(e, opt.value)}
                      className={classNames(
                        "flex items-center justify-center shrink-0",
                        "w-4 h-4 rounded-full",
                        "text-primary/60 hover:text-primary hover:bg-primary/20",
                        "transition-colors duration-150",
                      )}
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  )}
                </span>
              ))
            ) : (
              <span className="text-gray-400 text-base font-normal">
                {placeholder}
              </span>
            )}
          </div>
          <span
            className="relative flex items-center justify-center size-6 shrink-0 ml-2 [&>svg]:text-gray-500 transition-transform duration-200"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M6 9l6 6 6-6"></path>
            </svg>
          </span>
        </div>
      </div>

      {/* Floating Dropdown Menu */}
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-50"
            style={{
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`,
              width: `${popupPosition.width}px`,
              minWidth: `${popupPosition.width}px`,
            }}
          >
            <CListboxMulti
              value={value}
              onChange={onChange}
              onChangeCallback={onChangeCallback}
              options={options}
              isOptionDisabled={isOptionDisabled}
            />
          </div>,
          document.body,
        )}
    </CFormGroup>
  );
  //#endregion
};
